import { useArrMrr } from "@/services/context/ArrMrrContext";
import Text from "@/components/ui/text";
import { DealsMe } from "@/pages/deals/types";
import { formatNumber } from "@/utils/numbers";
import { capitalize, isNil } from "lodash";
import React from "react";
import { useTranslation } from "react-i18next";

interface Props {
  data?: DealsMe;
}

interface KPI {
  name: keyof DealsMe | "committedArrMrr";
  isMonetary?: boolean;
  unit?: string;
}

const TopKPIBar: React.FC<Props> = ({ data }) => {
  const { t } = useTranslation();
  const { label: arrMrrLabel, fieldName: arrOrMrrField } = useArrMrr();

  const kpis: KPI[] = [
    { name: "quota", isMonetary: true },
    { name: "pipelineCoverage", unit: "x" },
    { name: "committedArrMrr", isMonetary: true }
  ];

  const getCommittedArrOrMrrValue = (dealsMe: any) => {
    return dealsMe?.[`committed${capitalize(arrOrMrrField)}`];
  };

  const getKpiValue = (kpi: KPI) => {
    if (kpi.name === "committedArrMrr") {
      return getCommittedArrOrMrrValue(data);
    }
    return data?.[kpi.name as keyof DealsMe] || 0;
  };

  const renderNumber = (kpi: KPI) => {
    const { value, currencySign } = formatNumber(
      getKpiValue(kpi),
      0,
      kpi.isMonetary,
      false
    );
    return (
      <div className="flex items-baseline gap-[1px]">
        {kpi?.isMonetary && (
          <Text data-test={`bottom-kpi-${kpi.name}-currency`}>
            {currencySign}
          </Text>
        )}
        <Text variant="label" data-test={`top-kpi-${kpi.name}-value`}>
          {value}
        </Text>
      </div>
    );
  };

  return (
    <div className="flex items-center w-full gap-4 px-4 py-1">
      {kpis
        .filter((kpi) => {
          const kpiValue = getKpiValue(kpi);
          return !isNil(kpiValue);
        })
        .map((kpi) => (
          <div
            key={kpi.name}
            className="flex items-center gap-4 [&:not(:last-child)]:after:content-[''] [&:not(:last-child)]:after:h-4 [&:not(:last-child)]:after:w-[1px] [&:not(:last-child)]:after:bg-border-border2 [&:not(:last-child)]:after:mx-1"
          >
            <div className="flex items-baseline gap-[6px]">
              <Text
                data-test={`top-kpi-${kpi.name}-name`}
                variant="bodySecondary"
              >
                {t(`deals.topKPI.${kpi.name}`, { arrMrr: arrMrrLabel })}
              </Text>
              <div className="flex items-baseline">
                {renderNumber(kpi)}
                {kpi?.unit && (
                  <Text variant="label" className="pl-[1px]">
                    {kpi?.unit}
                  </Text>
                )}
              </div>
            </div>
          </div>
        ))}
    </div>
  );
};

export default TopKPIBar;
